/**
 * Safely parse a value to a number with fallback
 *
 * @param value - Value to parse (can be number, string, or unknown)
 * @param fallback - Fallback value if parsing fails (default: 0)
 * @returns Parsed number or fallback
 *
 * @example
 * parseNumberSafe(42) // 42
 * parseNumberSafe("42") // 42
 * parseNumberSafe("invalid") // 0
 * parseNumberSafe("invalid", -1) // -1
 * parseNumberSafe(NaN) // 0
 * parseNumberSafe("  ") // 0
 */
export const parseNumberSafe = (value: unknown, fallback: number = 0): number => {
  // Handle number type
  if (typeof value === 'number') {
    return isNaN(value) || !isFinite(value) ? fallback : value
  }

  // Handle string type
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return fallback

    const parsed = Number(trimmed)
    return isNaN(parsed) || !isFinite(parsed) ? fallback : parsed
  }

  // All other types (null, undefined, objects, etc.)
  return fallback
}

/**
 * Strictly parse a value to a number, returning null for invalid values
 * More explicit than parseNumberSafe - use when you need to distinguish
 * between real 0 and invalid data
 *
 * @param value - Value to parse
 * @returns Parsed number or null if invalid
 *
 * @example
 * parseNumberStrict(42) // 42
 * parseNumberStrict("42") // 42
 * parseNumberStrict("invalid") // null
 * parseNumberStrict(NaN) // null
 * parseNumberStrict("  ") // null
 * parseNumberStrict(0) // 0
 */
export const parseNumberStrict = (value: unknown): number | null => {
  // Handle number type
  if (typeof value === 'number') {
    return isNaN(value) || !isFinite(value) ? null : value
  }

  // Handle string type
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return null

    const parsed = Number(trimmed)
    return isNaN(parsed) || !isFinite(parsed) ? null : parsed
  }

  // All other types
  return null
}

/**
 * Parse integer safely with fallback
 * Specifically for integer values (no decimals)
 *
 * @param value - Value to parse
 * @param fallback - Fallback value if parsing fails (default: 0)
 * @returns Parsed integer or fallback
 */
export const parseIntSafe = (value: unknown, fallback: number = 0): number => {
  // Handle number type
  if (typeof value === 'number') {
    return isNaN(value) || !isFinite(value) ? fallback : Math.floor(value)
  }

  // Handle string type
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return fallback

    const parsed = parseInt(trimmed, 10)
    return isNaN(parsed) ? fallback : parsed
  }

  // All other types
  return fallback
}
